/**
 * space_patrols.ts — CIV PATROLS in the Commons Reach (2026-09-08: "friendly
 * ships and ships of the opposing civilization scattered in the commons
 * solar system alongside the pirate ships; the friendly ships attack the
 * enemy ships and both civilizations attack pirate ships").
 *
 * Pure: the posts (where each civ keeps a patrol), the spawn/cull window
 * around the player (the void-raider law — full-AI hulls only exist near
 * you), and ONE target rule for every hull in the system:
 *   · a civ hull hunts the OTHER civ's hulls AND every pirate hull (dens,
 *     raiders, promoted wings), the nearest first, and the player when the
 *     player opposes it;
 *   · a pirate hull keeps the pack law on the player inside its aggro range
 *     and otherwise turns on the nearest civ hull within PIRATE_VS_CIV_M —
 *     the two civs and the pirates are three sides of one fight.
 */
export type Side = 0 | 1 | -1;

export interface PatrolPost {
  key: string;
  civ: 'aurelian' | 'borean';
  side: 0 | 1;
  pos: [number, number, number];
  ships: number;
  tier: 1 | 2 | 3;
  /** The POI whose volume the post haunts (den aggro wakes it). */
  poiKey: string;
}

/** Posts spawn their hulls inside this range of the player … */
export const PATROL_SPAWN_M = 60_000;
/** … and shed them beyond this (hysteresis: never flapping at the edge). */
export const PATROL_CULL_M = 80_000;
/** Full-AI patrol hulls alive at once (both civs). */
export const PATROL_LIVE_MAX = 24;
/** A downed patrol hull re-forms at its post after this. */
export const PATROL_RESPAWN_MS = 60_000;
/** A civ hull engages what it can see inside this. */
export const PATROL_ENGAGE_M = 12_000;
/** A pirate turns on a civ hull inside this when the player is out of reach. */
export const PIRATE_VS_CIV_M = 6_000;

function h32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h << 13; h >>>= 0; h ^= h >> 17; h ^= h << 5; h >>>= 0;
  return h;
}

interface PoiLike { key: string; kind: string; pos: readonly [number, number, number] | readonly number[]; radius: number; pirates: number; tier: 1 | 2 | 3 }

/** Two posts per point of interest — one per civ, on opposite sides of it,
 *  clear of the den's own ground — so both civs patrol every region, meet
 *  each other there, and meet the pirates. Deterministic (hashed). */
export function civPatrolPosts(pois: readonly PoiLike[]): PatrolPost[] {
  const out: PatrolPost[] = [];
  for (const poi of pois) {
    const h = h32(poi.key);
    const ang = ((h % 3600) / 3600) * Math.PI * 2;
    const dist = poi.radius * 1.6 + 2_600 + (h >> 12) % 1200;
    const dy = 400 + ((h >> 8) % 500);
    const ships = poi.pirates >= 4 ? 3 : poi.pirates >= 1 ? 3 : 2;
    const tier: 1 | 2 | 3 = poi.tier >= 3 ? 3 : poi.tier === 2 ? 2 : (poi.kind === 'station' || poi.kind === 'megastation') ? 2 : 1;
    const civs: Array<['aurelian', 0] | ['borean', 1]> = [['aurelian', 0], ['borean', 1]];
    for (const [civ, side] of civs) {
      const a = ang + (side === 0 ? 0 : Math.PI);
      out.push({
        key: `patrol${side}:${poi.key}`, civ, side,
        pos: [poi.pos[0] + Math.cos(a) * dist, poi.pos[1] + (side === 0 ? dy : -dy), poi.pos[2] + Math.sin(a) * dist],
        ships, tier, poiKey: poi.key,
      });
    }
  }
  return out;
}

/** Which posts should have hulls (inside PATROL_SPAWN_M) and which must shed
 *  them (beyond PATROL_CULL_M), for a player at (x, y, z). */
export function postsInWindow(posts: readonly PatrolPost[], x: number, y: number, z: number, live: ReadonlySet<string>): { spawn: PatrolPost[]; cull: PatrolPost[] } {
  const spawn: PatrolPost[] = [], cull: PatrolPost[] = [];
  for (const p of posts) {
    const d = Math.hypot(p.pos[0] - x, p.pos[1] - y, p.pos[2] - z);
    if (d <= PATROL_SPAWN_M && !live.has(p.key)) spawn.push(p);
    else if (d > PATROL_CULL_M && live.has(p.key)) cull.push(p);
  }
  spawn.sort((a, b) => Math.hypot(a.pos[0] - x, a.pos[1] - y, a.pos[2] - z) - Math.hypot(b.pos[0] - x, b.pos[1] - y, b.pos[2] - z));
  return { spawn, cull };
}

export interface QuarryCand { side: Side; ally: boolean; bounty: boolean; dist: number }
export type QuarryPick = { kind: 'hull'; idx: number } | { kind: 'player' } | null;

/** The one target rule (see the header). `self.side` −1 = a pirate hull. */
export function pickQuarry(
  self: { side: Side; ally: boolean },
  cands: readonly QuarryCand[],
  player: { dist: number; hostile: boolean },
  aggroM: number,
): QuarryPick {
  if (self.side !== -1) {
    const enemy = (1 - self.side) as 0 | 1;
    let best = -1, bd = Infinity;
    for (let i = 0; i < cands.length; i++) {
      const c = cands[i];
      const target = c.side === enemy || (c.side === -1 && !c.bounty); // the other civ, or any pirate (a bounty hunter is the player's)
      if (!target || c.dist >= bd) continue;
      bd = c.dist; best = i;
    }
    const dp = player.hostile ? player.dist : Infinity;
    if (best >= 0 && bd <= dp && bd < PATROL_ENGAGE_M) return { kind: 'hull', idx: best };
    if (player.hostile && dp < PATROL_ENGAGE_M) return { kind: 'player' };
    return null;
  }
  // a pirate: the pack law on the player first, then the nearest civ hull
  if (player.dist <= aggroM) return { kind: 'player' };
  let best = -1, bd = Infinity;
  for (let i = 0; i < cands.length; i++) {
    const c = cands[i];
    if (c.side === -1 || c.dist >= bd) continue;
    bd = c.dist; best = i;
  }
  if (best >= 0 && bd < PIRATE_VS_CIV_M) return { kind: 'hull', idx: best };
  return { kind: 'player' }; // out of everything's reach: the old behaviour (the FSM idles past aggro)
}

/** May a bolt from `shooter` land on `target`? Never your own side; a civ
 *  bolt lands on the other civ and on pirates; a pirate bolt lands on both
 *  civs (and the player, handled by the caller). */
export function boltHits(shooter: { side: Side; ally: boolean }, target: { side: Side; ally: boolean; bounty: boolean }): boolean {
  if (shooter.side !== -1) {
    if (target.side === shooter.side) return false;
    if (target.side === -1) return !target.bounty; // pirates, not the player's hunter
    return true;
  }
  return target.side !== -1; // a pirate's bolt: any civ hull
}
